import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
  TableFooter,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  VisibilityOutlined,
  EditOutlined,
  DeleteOutline,
  InboxOutlined,
} from '@mui/icons-material';
import AppPagination from './AppPagination';

export default function DataTable({
  rows,
  columns,
  onView,
  onEdit,
  onDelete,
  actionGuard,
  showActions = true,
  pagination,
  loading = false,
  rowKey = (row) => row.id ?? JSON.stringify(row),
  maxHeight = 560,
  stickyActionsRight = true,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const derivedColumns = safeRows.length
    ? Object.keys(safeRows[0]).map((key) => ({ Header: key, accessor: key }))
    : [];
  const cols = Array.isArray(columns) && columns.length ? columns : derivedColumns;
  const hasAnyAction = Boolean(onView || onEdit || onDelete);
  const showActionsCol = showActions && hasAnyAction;

  const hasPagination = Boolean(pagination && pagination.total != null);
  const page0 = hasPagination ? Math.max(0, (pagination.page || 1) - 1) : 0;
  const pageSize = hasPagination ? pagination.pageSize || 50 : 50;
  const total = hasPagination ? pagination.total || 0 : 0;

  const handleChangePage = (_event, newPage0) => {
    pagination?.onPageChange?.(newPage0 + 1);
  };

  const stickyCellSx = (col, isHeader = false) => {
    const side = col?.sticky;
    if (side !== 'left' && side !== 'right') return {};

    return {
      position: 'sticky',
      [side]: col?.stickyOffset ?? 0,
      backgroundColor: (theme) =>
        isHeader ? theme.palette.background.default : theme.palette.background.paper,
      zIndex: isHeader ? 7 : 4,
      boxShadow:
        side === 'left'
          ? '2px 0 5px -4px rgba(15, 23, 42, 0.38)'
          : '-2px 0 5px -4px rgba(15, 23, 42, 0.38)',
    };
  };

  const actionsStickySx = stickyActionsRight
    ? {
        position: 'sticky',
        right: 0,
        backgroundColor: (theme) => theme.palette.background.paper,
        zIndex: 4,
        boxShadow: '-2px 0 5px -4px rgba(15, 23, 42, 0.38)',
      }
    : {};

  return (
    <TableContainer
      sx={{
        position: 'relative',
        maxHeight,
        overflow: 'auto',
        borderRadius: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
        boxShadow: (theme) =>
          theme.palette.mode === 'light' ? '0 8px 24px rgba(15, 23, 42, 0.045)' : 'none',
        scrollBehavior: 'smooth',
        '& thead': { backgroundColor: (theme) => theme.palette.background.default },
      }}
    >
      <Table
        stickyHeader
        size="small"
        aria-label="Tabla de resultados"
        sx={{
          position: 'relative',
          minWidth: 'max-content',
          tableLayout: 'auto',
          '& thead th': { fontWeight: 700, whiteSpace: 'nowrap', zIndex: 6 },
          '& tbody td': { py: 1.25, verticalAlign: 'middle' },
          '& tbody tr:hover': { bgcolor: (theme) => theme.palette.action.hover },
        }}
      >
        <TableHead>
          <TableRow>
            {cols.map((col, index) => (
              <TableCell
                key={col.id || col.Header || index}
                align={col.align || 'left'}
                sx={{
                  width: col.width,
                  minWidth: col.width,
                  ...stickyCellSx(col, true),
                }}
              >
                {col.Header}
              </TableCell>
            ))}
            {showActionsCol && (
              <TableCell
                align="center"
                sx={{ minWidth: 122, fontWeight: 700, ...actionsStickySx, zIndex: 7 }}
              >
                Acciones
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading &&
            Array.from({ length: Math.min(pageSize, 6) }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {cols.map((col, colIndex) => (
                  <TableCell
                    key={`${col.id || col.Header || colIndex}-${index}`}
                    sx={{ width: col.width, minWidth: col.width, ...stickyCellSx(col) }}
                  >
                    <Skeleton />
                  </TableCell>
                ))}
                {showActionsCol && (
                  <TableCell align="center" sx={actionsStickySx}>
                    <Skeleton width={76} sx={{ mx: 'auto' }} />
                  </TableCell>
                )}
              </TableRow>
            ))}

          {!loading &&
            safeRows.map((row) => {
              const can = {
                view: true,
                edit: true,
                delete: true,
                ...(actionGuard?.(row) || {}),
              };

              return (
                <TableRow key={rowKey(row)} hover>
                  {cols.map((col, index) => {
                    const value =
                      typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor];

                    return (
                      <TableCell
                        key={col.id || col.Header || index}
                        align={col.align || 'left'}
                        sx={{
                          width: col.width,
                          minWidth: col.width,
                          ...stickyCellSx(col),
                        }}
                      >
                        {value ?? ''}
                      </TableCell>
                    );
                  })}

                  {showActionsCol && (
                    <TableCell align="center" sx={actionsStickySx}>
                      {onView && can.view && (
                        <Tooltip title="Ver detalle">
                          <IconButton
                            aria-label="Ver detalle"
                            color="primary"
                            size="small"
                            onClick={() => onView(row)}
                          >
                            <VisibilityOutlined fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onEdit && can.edit && (
                        <Tooltip title="Editar">
                          <IconButton
                            aria-label="Editar"
                            color="primary"
                            size="small"
                            onClick={() => onEdit(row)}
                          >
                            <EditOutlined fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDelete && can.delete && (
                        <Tooltip title="Eliminar o cancelar">
                          <IconButton
                            aria-label="Eliminar o cancelar"
                            color="error"
                            size="small"
                            onClick={() => onDelete(row)}
                          >
                            <DeleteOutline fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

          {!loading && safeRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={Math.max(1, cols.length + (showActionsCol ? 1 : 0))}>
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <InboxOutlined color="disabled" sx={{ fontSize: 32, mb: 0.75 }} />
                  <Typography variant="body1" fontWeight={700}>
                    Aún no hay datos para mostrar
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Prueba ajustando la búsqueda o los filtros.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        {hasPagination && (
          <TableFooter>
            <TableRow>
              <AppPagination
                inTable
                count={total}
                page={page0}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[pageSize]}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}

DataTable.propTypes = {
  actionGuard: PropTypes.func,
};
